import { get } from "@/utils/utils";
import { useEffect, useMemo, useState } from "react";
import type { GuessItem } from "../types";

function isSameGuessNos( prev: string[], next: string[] ) {
    return prev.length === next.length && prev.every( ( value, index ) => value === next[index] );
}

/**
 * 管理图例过滤状态
 * @param guessList - 竞猜列表
 * @param eventNo - 事件编号
 * @returns 选中的竞猜编号、设置函数、显示的竞猜列表
 */
export function useLegendFilterState(
    guessList: GuessItem[],
    eventNo: string | null,
) {
    const [ selectedGuessNos, setSelectedGuessNos ] = useState<string[]>( [] );

    // 过滤出 status 不为 3 的可展示项
    const displayableGuessList = useMemo( () => {
        return guessList.filter( ( guess ) => {
            const status = get( guess, "status", 0 );
            return status !== 3;
        } );
    }, [ guessList ] );

    // 初始化：如果超过4个，默认选中前4个（只从可展示的项中选择）
    useEffect( () => {
        const nextSelectedGuessNos = displayableGuessList.length > 0
            ? ( displayableGuessList.length > 4
                ? displayableGuessList.slice( 0, 4 ).map( ( g ) => get( g, "guessNo", "" ) )
                : displayableGuessList.map( ( g ) => get( g, "guessNo", "" ) ) )
            : [];

        setSelectedGuessNos( ( prev ) => isSameGuessNos( prev, nextSelectedGuessNos ) ? prev : nextSelectedGuessNos );
    }, [ eventNo, displayableGuessList ] );

    // 过滤显示的竞猜列表（只显示 status 不为 3 的项）
    const displayedGuessList = useMemo( () => {
        if ( displayableGuessList.length <= 4 ) {
            return displayableGuessList;
        }
        return displayableGuessList.filter( ( guess ) => selectedGuessNos.includes( get( guess, "guessNo", "" ) ) );
    }, [ displayableGuessList, selectedGuessNos ] );

    return {
        selectedGuessNos,
        setSelectedGuessNos,
        displayedGuessList,
    };
}
